import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { container } from "tsyringe";
import { useNavigateToHomePage } from "hooks/useNavigateToHomePage";
import { useProjectId } from "hooks/useProjectId";
import ProjectTeamService from "services/projects/TeamService";
import ProjectTeamResponseResource from "common/resources/Project/Config/Team/ProjectTeamResponseResource";

const projectTeamService = container.resolve(ProjectTeamService);

export type IProjectTeamContext = {
	projectTeamId: string;
};

type IProps = {
	children: ReactNode;
};

const ProjectTeamContext = createContext<IProjectTeamContext>(undefined!);

export function ProjectTeamProvider(props: IProps) {
	const { projectTeam } = useProjectTeam();

	return (
		projectTeam && (
			<ProjectTeamContext.Provider
				value={{
					projectTeamId: projectTeam.id,
				}}>
				{props.children}
			</ProjectTeamContext.Provider>
		)
	);
}

export const useProjectTeamContext = () => {
	const context = useContext(ProjectTeamContext);

	if (context === undefined) {
		throw new Error("useProjectTeamContext must be used within a ProjectTeamProvider");
	}

	return context;
};

const useProjectTeam = () => {
	const { navigateToHomePage } = useNavigateToHomePage();
	const { projectId } = useProjectId();
	const [projectTeam, setProjectTeam] = useState<ProjectTeamResponseResource | null>(null);

	const fetchProjectTeam = useCallback(async () => {
		projectTeamService
			.getByProjectId(projectId)
			.then(setProjectTeam)
			.catch(() => {
				console.error("Failed to fetch project team");
				navigateToHomePage();
			});
	}, [navigateToHomePage, projectId]);

	useEffect(() => {
		fetchProjectTeam();
	}, [fetchProjectTeam]);

	return {
		projectTeam: projectTeam,
	};
};
